/**
 * Compress an image File or Data URL to a lightweight JPEG Data URL.
 * Resizes the image to fit within maxWidth/maxHeight and applies JPEG compression.
 * This ensures uploaded profile pictures do not exceed browser localStorage quota.
 */
export function compressImage(
  source: File | string,
  maxWidth = 250,
  maxHeight = 250,
  quality = 0.7
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(typeof source === 'string' ? source : '');
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      // Export as compact JPEG
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedDataUrl);
    };

    img.onerror = () => {
      resolve(typeof source === 'string' ? source : '');
    };

    if (source instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = () => resolve('');
      reader.readAsDataURL(source);
    } else if (typeof source === 'string') {
      img.src = source;
    } else {
      resolve('');
    }
  });
}
